'use client';
import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import type { AssetTypeType } from '@terra/shared-schemas-orchestrator';
import type {
  CvssEnvironmentalRequirements,
  CvssVersion,
} from '@terra/shared-cvss';
import type { SearchRunResult } from '../../findings/FindingEditDrawer';

export type SearchSignalResult = {
  id: string;
  label: string;
  category?: string;
  status?: string | null;
  description?: string;
  createdAt?: string;
  endpointId?: string;
  harEntryId?: string;
  entryPointId?: string;
  url?: string;
  method?: string;
  isAppWide?: boolean;
  origin?: string;
};

export type SearchAssetResult = {
  id: string;
  url: string;
  method?: string;
  type: AssetTypeType;
};

export type SearchHarEntryResult = {
  id: string;
  method?: string;
  url?: string;
  status?: number;
  createdAt?: string;
};

type ChatFindingSearchContextType = {
  onSearchSignals?: (query: string) => Promise<SearchSignalResult[]>;
  onGetSignalById?: (id: string) => Promise<SearchSignalResult | null>;
  onSearchRuns?: (query: string) => Promise<SearchRunResult[]>;
  onSearchAssets?: (query: string) => Promise<SearchAssetResult[]>;
  onGetEndpointById?: (id: string) => Promise<SearchAssetResult | null>;
  onGetHarEntriesByEndpoint?: (
    endpointId: string,
  ) => Promise<SearchHarEntryResult[]>;
  defaultCvssVersion?: CvssVersion;
  cvssEnvironmentalRequirements?: Partial<CvssEnvironmentalRequirements> | null;
};

const ChatFindingSearchContext = createContext<
  ChatFindingSearchContextType | undefined
>(undefined);

type ChatFindingSearchProviderProps = {
  children: ReactNode;
  onSearchSignals?: (query: string) => Promise<SearchSignalResult[]>;
  onGetSignalById?: (id: string) => Promise<SearchSignalResult | null>;
  onSearchRuns?: (query: string) => Promise<SearchRunResult[]>;
  onSearchAssets?: (query: string) => Promise<SearchAssetResult[]>;
  onGetEndpointById?: (id: string) => Promise<SearchAssetResult | null>;
  onGetHarEntriesByEndpoint?: (
    endpointId: string,
  ) => Promise<SearchHarEntryResult[]>;
  defaultCvssVersion?: CvssVersion;
  cvssEnvironmentalRequirements?: Partial<CvssEnvironmentalRequirements> | null;
};

export function ChatFindingSearchProvider({
  children,
  onSearchSignals,
  onGetSignalById,
  onSearchRuns,
  onSearchAssets,
  onGetEndpointById,
  onGetHarEntriesByEndpoint,
  defaultCvssVersion,
  cvssEnvironmentalRequirements,
}: ChatFindingSearchProviderProps) {
  const contextValue: ChatFindingSearchContextType = useMemo(
    () => ({
      onSearchSignals,
      onGetSignalById,
      onSearchRuns,
      onSearchAssets,
      onGetEndpointById,
      onGetHarEntriesByEndpoint,
      defaultCvssVersion,
      cvssEnvironmentalRequirements,
    }),
    [
      onSearchSignals,
      onGetSignalById,
      onSearchRuns,
      onSearchAssets,
      onGetEndpointById,
      onGetHarEntriesByEndpoint,
      defaultCvssVersion,
      cvssEnvironmentalRequirements,
    ],
  );

  return (
    <ChatFindingSearchContext.Provider value={contextValue}>
      {children}
    </ChatFindingSearchContext.Provider>
  );
}

export function useFindingSearchContext() {
  const context = useContext(ChatFindingSearchContext);
  // Return empty object if not within provider - makes it optional
  return context ?? {};
}
